import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NavigationStart, Router } from '@angular/router';
import { MetadataFormService } from '@app/core/services/metadata/metadata-form.service';
import { Form } from '@app/modules/form-generator/edit-form-v3/models/form.model';
import { EmployeeTableSecurity } from '@app/shared/models/employee.model';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { EmployeeSecurityService } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-security/services/employee-security.service';
import { Culture } from '@app/shared/models/system-language/culture.model';
import { ChangeReasonDialogComponent } from '@app/shared/components/change-reason-dialog/change-reason-dialog.component';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { GenerateScheduleService } from '@app/modules/generate-schedules/services/generate-schedule.service';
import moment from 'moment';

@Component({
  selector: 'app-employee-absence-dialog',
  templateUrl: './employee-absence-dialog.component.html',
  styleUrls: ['./employee-absence-dialog.component.scss']
})
export class EmployeeAbsenceDialogComponent implements OnInit {
  @Input() formTitle: string;
  @Input() formId: string;
  @Input() changeReasonFormId: string;
  @Input() formData: any;
  @Output() emitFormData: EventEmitter<void> = new EventEmitter<void>();
  
  getFormData: boolean = false;
  formValid: boolean = false;
  loadingFormDetails: boolean = true;
  formPristine: boolean = true;
  formDetails: Form;
  targetEmployeeTablePermissions: EmployeeTableSecurity[];
  loadingCultures: boolean;
  currentCulture: Culture;
  hideWorkingHours: boolean;
  employeeId: string;
  workingHours: number;
  endDate: any;
  startDate: any;
  startTime: string = null;
  endTime: string = null;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private metadataFormService: MetadataFormService,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<EmployeeAbsenceDialogComponent>,
    private employeeSecurityService: EmployeeSecurityService,
    private generateScheduleService: GenerateScheduleService,
    @Inject(MAT_DIALOG_DATA) data)
  {
    this.formTitle = data.formTitle;
    this.formId = data.formId;
    this.changeReasonFormId = data.changeReasonFormId;
    this.formData = data.formData;
    this.employeeId = data.employeeId;
    this.targetEmployeeTablePermissions = data.targetEmployeeTablePermissions;

    router.events.forEach((event) => {
      if(event instanceof NavigationStart) {
        this.dialogRef.close();
      }
      // NavigationEnd
      // NavigationCancel
      // NavigationError
      // RoutesRecognized
    });
  } 

  ngOnInit(): void {
    this.getCurrentCulture();
    this.getFormDetails();
  }

  // Get the form details to find the table assigned to the form 
  getFormDetails() {
    this.loadingFormDetails = true;
    
    this.metadataFormService.getForm(this.formId)
    .pipe(
      finalize(() => this.loadingFormDetails = false)
    )
    .subscribe( 
      res => {
        this.formDetails = res; 
      }
    );
  }

  get tableReadAccessObj() {
    return {
      tableId: this.formDetails.table ? this.formDetails.table.id : 'no-table',
      permission: 'read',
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions
    }
  }

  get tableCreateAccessObj() {
    return {
      tableId: this.formDetails.table ? this.formDetails.table.id : 'no-table',
      permission: 'create',
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions
    }
  }

  get tableUpdateAccessObj() {
    return {
      tableId: this.formDetails.table ? this.formDetails.table.id : 'no-table',
      permission: 'update',
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions
    }
  }

  get tableDeleteAccessObj() {
    return {
      tableId: this.formDetails.table ? this.formDetails.table.id : 'no-table',
      permission: 'delete',
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions
    }
  }

  get currentCultureFormName() {
    if(this.loadingCultures === false) {
      if(this.currentCulture) {
        return this.formDetails?.name.find(t => t.culture === this.currentCulture?.id)?.text;
      }
      else {
        return this.formDetails?.name.find(t => t.culture === 'en-GB')?.text;
      }
    }
  }

  getCurrentCulture() {
    this.loadingCultures = true;

    this.employeeSecurityService.getCurrentEmployeeCulture()
    .pipe(
      finalize(() => this.loadingCultures = false)
    )
    .subscribe(
      res => {
        this.currentCulture = res;
      }
    )
  }

  getEmployeeWorkingHours() {
    this.hideWorkingHours = true;

    if(this.startTime !== null) {
      // Set the start date to the start time
      this.startDate = moment.utc(this.startDate).set({
        hour: parseInt(this.startTime.split(':')[0]),
        minute: parseInt(this.startTime.split(':')[1])
      }).format('YYYY-MM-DD HH:mm:ss');
    }

    if(this.endTime !== null) {
      // Set the end date to the end time
      this.endDate = moment.utc(this.endDate).set({
        hour: parseInt(this.endTime.split(':')[0]),
        minute: parseInt(this.endTime.split(':')[1])
      }).format('YYYY-MM-DD HH:mm:ss');
    }

    this.generateScheduleService.getEmployeeWorkingHours(this.employeeId, this.startDate, this.endDate)
    .subscribe(
      {
        next: (v) => {
          this.workingHours = v.workingHours;
          this.hideWorkingHours = false;
        },
        error: (e) => {
          this.workingHours = null;
          this.hideWorkingHours = true;
        },
        complete: () => {}
      }
    )
  }

  openChangeReasonDialog(formData: any) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formId: this.changeReasonFormId,
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions,
    };

    const dialogRef = this.dialog.open(ChangeReasonDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data) {
          this.addChangeReasonsToFormDataAndEmit(formData, data);
        }
      }
    );
  }

  openConfirmCloseDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('UnsavedChangesMessage')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.dialogRef.close();
        }
      }
    ); 
  }

  addChangeReasonsToFormDataAndEmit(formData: any, changeReasonFormData: any) {
    let merged = {...formData, ...changeReasonFormData};
    this.checkHoursAndEmitFormData(merged);
  }

  formDataChanged(formData: any) {
    this.startTime = formData.startTime;
    this.endTime = formData.endTime;
    // calculate the working hours if the start date or end date has changed
    if (formData.hours !== null || formData.startDate === null || formData.endDate === null) {
      this.startDate = null;
      this.endDate = null;
      this.workingHours = null;
      return;
    }

    if (formData.startDate !== this.startDate || formData.endDate !== this.endDate || formData.startTime || formData.endTime) {
      if (typeof formData.startDate !== 'string') {
        this.startDate = moment(formData.startDate).format('YYYY-MM-DD');
      } else if (typeof formData.startDate === 'string') {
        this.startDate = formData.startDate;
      }
      if (typeof formData.endDate !== 'string') {
        this.endDate = moment(formData.endDate).format('YYYY-MM-DD');
      } else if (typeof formData.endDate === 'string') {
        this.endDate = formData.endDate;
      }

      this.getEmployeeWorkingHours();
    }
  }

  formDataEmitted(formData) {
    //if there is no change reason form id emit form data, if there is open the change reason dialog
    if(this.changeReasonFormId === undefined){
      this.checkHoursAndEmitFormData(formData);
    }
    else {
      this.openChangeReasonDialog(formData);
    }
  }

  checkHoursAndEmitFormData(formData) {
    if(formData.hours === null) {
      formData.hours = this.workingHours;
    }
    this.emitFormData.emit(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  formPristineEmitted(formPristine: boolean) {
    this.formPristine = formPristine;
  }

  close(){
    this.formPristine ? this.dialogRef.close() : this.openConfirmCloseDialog();
  }

}